import { safely } from '@epihub/core/src/fn/safely';
import { _isPlainObject } from '@epihub/core/src/fn/object';
import { safeStringifyCircular } from '@epihub/core/src/fn/stableStringify';
import { UNKNOWN_ERROR_MESSAGE, UNKNOWN_ERROR_NAME, UNKNOWN_ERROR_SCOPE } from './const';
import { knownErrorRegistry } from './registry';
import { JSError } from './JSError';

export function normalizeError(data: any): JSError {
  if (!data) {
    return treatAsUnknown(data);
  }

  if (data instanceof JSError) {
    // Already a JSError2 instance
    return data;
  }

  if (data instanceof Error) {
    // If this was a JSError then the 'message' field
    // will be the stringified version of JSError
    // Try parse the message
    const parsedMessage = safely(() => JSON.parse(data.message));
    if (parsedMessage && typeof parsedMessage === 'object' && '__isJSError' in parsedMessage) {
      return normalizeError(parsedMessage);
    }

    return treatAsUnknown(data);
  }

  if (_isPlainObject(data)) {
    if (data.__isJSError) {
      // Reconstruct JSError2 from plain object
      const {
        __isJSError,
        name,
        scope,
        message,
        parent,
        shouldSkipLogs,
        stack,
        ...extras
      } = data;

      const err = new JSError({
        name: name || UNKNOWN_ERROR_NAME,
        scope: scope || UNKNOWN_ERROR_SCOPE,
        message: message || UNKNOWN_ERROR_MESSAGE('Plain object with empty message'),
        parent,
        shouldSkipLogs,
        ...extras,
      });

      if (stack) {
        err.stack = stack;
      }

      return err;
    }

    // Plain object but not a JSError2
    return treatAsUnknown(data);
  }

  if (typeof data === 'string') {
    // Try to parse as JSON
    const parsed = safely(() => JSON.parse(data));

    if (parsed && typeof parsed === 'object') {
      return normalizeError(parsed);
    }

    // Just a string, not JSON
    return treatAsUnknown(data);
  }

  return treatAsUnknown(data);
}

function treatAsUnknown(data: any) {
  for (const knownError of knownErrorRegistry.knownErrors) {
    const mapped = safely(() => knownError.handle(data));
    if (mapped) {
      return mapped;
    }
  }

  if (data instanceof Error) {
    const err = new JSError({
      name: data.name || UNKNOWN_ERROR_NAME,
      scope: UNKNOWN_ERROR_SCOPE,
      message: data.message || UNKNOWN_ERROR_MESSAGE('treatAsUnknown Error'),
      parent: data,
    });

    if (data.stack) {
      err.stack = data.stack;
    }

    return err;
  }

  return new JSError({
    name: UNKNOWN_ERROR_NAME,
    scope: UNKNOWN_ERROR_SCOPE,
    message: data
      ? safeStringifyCircular(data)
      : UNKNOWN_ERROR_MESSAGE('treatAsUnknown'),
    parent: data,
  });
}
